import { useContext, useState } from 'react';

import {
  ScrollableTabs,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ThemeModeContext } from '../casePresentation/patientDownloadable/PreviewDialog/QRPrintDownloadContent';
import { useAdultTreatmentOptions } from '../casePresentation/patientDownloadable/TreatmentOptions';

import { TreatmentTabContent } from './TreatmentTabContent';

export type AdultTreatmentKey =
  | 'filling'
  | 'srp'
  | 'crown'
  | 'rct'
  | 'tooth_remineralization'
  | 'implant'
  | 'aligner'
  | 'bridge'
  | 'perio_carriers'
  | 'periodontal_maintenance';

export interface AdultTreatmentOption {
  description: string;
  fallback: string;
  label: string;
  src: string;
  value: AdultTreatmentKey;
  isPediatric: false;
  isVideo?: boolean;
}

interface Props {
  imageId?: string;
}

export const AdultTabs = ({ imageId }: Props) => {
  const [tab, setTab] = useState<AdultTreatmentKey>('filling');

  const tabs = useAdultTreatmentOptions();
  const { trackEvent } = useTrackEvent();
  const mode = useContext(ThemeModeContext);
  const {
    isIframe,
    externalPatientId,
    externalImageId,
    patientId,
    trialStatus,
    centralizedBilling: isCentralizedBilling,
  } = useContext(ExternalPatientContext);
  const { id: selectedPatientId } = useParams<{ id: string }>();

  return (
    <Tabs
      value={tab}
      onValueChange={(value) => {
        setTab(value as AdultTreatmentKey);
        trackEvent(mixpanelEvents.patientEducationTreatmentTabChange, {
          patientId: selectedPatientId ?? patientId,
          imageId,
          isIframe: !!isIframe,
          externalPatientId,
          externalImageId,
          tab: value,
          trialStatus,
          centralizedBilling: isCentralizedBilling,
        });
      }}
    >
      <ScrollableTabs mode={mode} dependencies={[tabs]}>
        <TabsList
          className={classNames(
            'vai-bg-transparent vai-border-b vai-rounded-none vai-pb-0 vai-px-0 vai-h-auto vai-whitespace-nowrap vai-inline-flex',
            {
              'vai-border-zinc-700': mode === 'dark',
              'vai-border-zinc-200': mode === 'light',
            },
          )}
        >
          {Object.values(tabs).map(({ label, value }) => (
            <TabsTrigger
              className={classNames(
                'vai-pt-2 vai-pb-3 data-[state=active]:vai-bg-transparent vai-border-b-2 vai-border-transparent data-[state=active]:vai-border-[#2368F6] !vai-rounded-none !vai-shadow-none',
                {
                  'data-[state=active]:vai-text-zinc-50 vai-text-zinc-400':
                    mode === 'dark',
                  'data-[state=active]:vai-text-zinc-900 vai-text-zinc-500':
                    mode === 'light',
                },
              )}
              key={value}
              value={value}
              data-testid={`tab-trigger-adult-${value}`}
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
      </ScrollableTabs>
      {Object.values(tabs).map(({ value, fallback, src, isVideo }) => (
        <TreatmentTabContent
          fallback={fallback}
          key={value}
          tabKey={value}
          src={src}
          isVideo={isVideo}
        />
      ))}
    </Tabs>
  );
};
